import { isoDateTime, z } from "../zod.js";
import { Problem } from "../common/problem.js";
import { PlanningDate, PlanningPositiveQty, PlanningQty } from "./schemas.js";

// Module 03 planning: the promise contract (spec 03 §2.20, §3.7 as built,
// §4.9 and §5 rows 74 to 78; D-461 to D-464). P3-J J-1 builds the quote, the
// commit judged on a fresh probe, the withdraw of a quoted promise and the
// two reads. Quantities are in the product's base unit as decimal strings
// with three places, dates are the quoting facility's business dates, and no
// promise carries money (D-464 ruling 5). Lists answer `{items, next_cursor}`.

const Uuid = z.guid();
const Instant = isoDateTime({ offset: true });
const PageCursor = z.string().optional();
const PageLimit = z.coerce.number().int().min(1).max(200).optional();

// --- enums --------------------------------------------------------------------------

/** §2.20's seven statuses (D-463). */
export const PromiseStatus = z.enum([
  "quoted",
  "committed",
  "kept",
  "at_risk",
  "broken",
  "withdrawn",
  "expired",
]);
/** The statuses a live promise holds, the list's default filter. */
export const PROMISE_LIVE_STATUSES = ["quoted", "committed", "at_risk"] as const;
/** How the date was reached (§3.7 as built, paragraphs 2 to 4). */
export const PromiseFulfillmentPath = z.enum(["offcut", "stock", "production", "transfer"]);
/** Who wrote it (D-463). */
export const PromiseSource = z.enum(["dialog", "agent", "confirmation", "re_promise"]);
/** The watch's reasons (§3.11), first match wins (D-466 ruling 3). */
export const PromiseRiskReason = z.enum([
  "stock_consumed",
  "material_delay",
  "capacity_loss",
  "schedule_slip",
]);
/** Why a promise was withdrawn (D-466 ruling 6, D-469). */
export const PromiseWithdrawnReasonKey = z.enum([
  "manual",
  "re_promised",
  "order_cancelled",
  "line_cancelled",
]);
/** How the confirmation drain ended a line's request (spec 03 §3.7a, D-465 ruling 2). */
export const PromiseRequestOutcome = z.enum([
  "committed",
  "no_date",
  "already_promised",
  "gave_up",
]);
/** Where a component is ready from (D-461 ruling 6). */
export const PromiseMaterialSource = z.enum(["on_hand", "planned_order", "lead_time"]);
/** Why no date could be found (§3.7 as built, paragraph 5). */
export const CtpUnavailableReason = z.enum([
  "no_routing",
  "no_bom",
  "facility_calendar_missing",
  "too_many_operations",
  "probe_unstable",
]);

// --- the basis (§2.20) --------------------------------------------------------------

export const PromiseOffcutUnit = z
  .object({
    stock_unit_id: Uuid,
    /**
     * The piece's ledger balance in the product's base unit, never its own
     * `remaining_measure` (spec 03 §3.7 as built, paragraph 2).
     */
    length_m: PlanningQty,
  })
  .openapi("PromiseOffcutUnit");

export const PromiseCapacityWindow = z
  .object({
    work_center_id: Uuid,
    window_start: Instant,
    window_end: Instant,
  })
  .openapi("PromiseCapacityWindow");

export const PromiseMaterialAssumption = z
  .object({
    product_id: Uuid,
    source: PromiseMaterialSource,
    /** The planned order a `planned_order` assumption names; null otherwise. */
    ref_id: Uuid.nullable(),
    available_date: PlanningDate,
  })
  .openapi("PromiseMaterialAssumption");

/**
 * The assumption snapshot (§2.20): ids, dates and quantities only, never
 * money or margin. `facility_ids.source` is set for a transfer promise.
 */
export const PromiseBasis = z
  .object({
    schedule_id: Uuid.nullable(),
    schedule_version: z.number().int().min(1).nullable(),
    facility_ids: z.object({ quoting: Uuid, source: Uuid.nullable() }),
    atp: z.object({
      on_hand_used: PlanningQty,
      offcut_combination: z.array(PromiseOffcutUnit),
      in_transit_used: PlanningQty,
    }),
    capacity: z.array(PromiseCapacityWindow),
    /** The existing supply a receipt-covered promise rides (D-461 ruling 4b). */
    supply_ref: z.object({ kind: z.enum(["work_order", "planned_order"]), id: Uuid }).nullable(),
    material_assumptions: z.array(PromiseMaterialAssumption),
    safety_margin_days: z.number().int().min(0),
    capacity_beyond_horizon: z.boolean(),
    /** The probe run behind a capacity date (D-461 ruling 11); null when stock alone answered. */
    probe: z
      .object({
        run_id: Uuid,
        passes: z.number().int().min(1),
        facts_hash: z.string(),
      })
      .nullable(),
  })
  .openapi("PromiseBasis");

/** The watch's verdict per assumption, which the drawer renders (§4.9). */
export const PromiseBasisVerdict = z
  .object({
    kind: z.enum(["offcut_unit", "capacity_window", "material", "supply"]),
    ref: z.string(),
    holds: z.boolean(),
    reason_key: z.string().nullable(),
  })
  .openapi("PromiseBasisVerdict");

/** §2.20's verdict, written by the watch from J-3 (D-467). */
export const PromiseVerdict = z
  .object({
    verdict: z.enum(["kept", "broken"]),
    judged_qty: PlanningQty,
    shipped_on_time_qty: PlanningQty,
    shortfall_qty: PlanningQty,
    slip_days: z.number().int().min(0),
    pct_shipped_on_time: z.string(),
    downgraded: z.boolean(),
    shipped_date: PlanningDate.nullable(),
    corrected: z.boolean(),
    corrected_at: Instant.nullable(),
  })
  .openapi("PromiseVerdict");

// --- the promise (§2.20, rows 74 to 78) ---------------------------------------------

export const CtpPromise = z
  .object({
    id: Uuid,
    promise_no: z.string(),
    facility_id: Uuid,
    customer_id: Uuid.nullable(),
    sales_order_id: Uuid.nullable(),
    sales_order_line_id: Uuid.nullable(),
    quote_ref: z.string().nullable(),
    /** The quote line whose Check date wrote the promise (J-2, D-465 ruling 5); null otherwise. */
    quote_line_id: Uuid.nullable(),
    product_id: Uuid,
    /** In the product's base unit. */
    qty: PlanningQty,
    requested_date: PlanningDate,
    earliest_date: PlanningDate,
    /** `max(requested_date, earliest_date)`: never earlier than asked (D-461 ruling 9). */
    promised_date: PlanningDate,
    feasible_by_requested: z.boolean(),
    fulfillment_path: PromiseFulfillmentPath,
    source: PromiseSource,
    status: PromiseStatus,
    basis: PromiseBasis,
    current_projected_date: PlanningDate.nullable(),
    risk_reason: PromiseRiskReason.nullable(),
    flagged_at: Instant.nullable(),
    resolved_at: Instant.nullable(),
    verdict: PromiseVerdict.nullable(),
    basis_verdicts: z.array(PromiseBasisVerdict).nullable(),
    watch_checked_at: Instant.nullable(),
    /** A quote's 72-hour validity; null once it is no longer quoted. */
    valid_until: Instant.nullable(),
    /** The module 06 holds this promise's commit minted (D-462 ruling 5). */
    reserved_reservation_ids: z.array(Uuid),
    /** The fresh quote a `BASIS_STALE` commit wrote (D-462 ruling 3). */
    superseded_by_promise_id: Uuid.nullable(),
    withdrawn_reason_key: PromiseWithdrawnReasonKey.nullable(),
    withdrawn_reason: z.string().nullable(),
    committed_at: Instant.nullable(),
    committed_by: Uuid.nullable(),
    promised_by: Uuid,
    created_at: Instant,
    updated_at: Instant,
    /**
     * Module 09's labels for the reader (§7's narrowing): the customer's name
     * and the order number with its line, null where the reader's doors do
     * not answer them, which renders "Sales order".
     */
    customer_label: z.string().nullable(),
    order_label: z.string().nullable(),
    /** The product's SKU and name as module 01 serves them. */
    product_label: z.string().nullable(),
  })
  .openapi("CtpPromise");

/** The off-cut units a quote found, which module 09 prices (D-461 ruling 3). */
export const CtpOffcutOption = z
  .object({
    units: z.array(PromiseOffcutUnit),
    /** Σ the units' lengths less the quantity asked, in the product's base unit. */
    waste_qty: PlanningQty,
  })
  .openapi("CtpOffcutOption");

/** Row 74's 201: the quoted promise and, when the off-cut path answered, its units. */
export const CtpQuoteResult = CtpPromise.extend({
  offcut_option: CtpOffcutOption.nullable(),
}).openapi("CtpQuoteResult");

export const CtpPromiseTimelineEntry = z
  .object({
    at: Instant,
    status: PromiseStatus,
    reason_key: z.string().nullable(),
  })
  .openapi("CtpPromiseTimelineEntry");

/** Row 78: the promise with its timeline. */
export const CtpPromiseDetail = CtpPromise.extend({
  timeline: z.array(CtpPromiseTimelineEntry),
}).openapi("CtpPromiseDetail");

// --- requests -----------------------------------------------------------------------

/** Row 74. `qty` is in the product's base unit; `pinned_units` is the drain's and never on the wire. */
export const CtpQuoteRequest = z
  .object({
    facility_id: Uuid,
    product_id: Uuid,
    qty: PlanningPositiveQty,
    requested_date: PlanningDate,
    sales_order_line_id: Uuid.optional(),
    quote_ref: z.string().trim().min(1).max(64).optional(),
    /** The composer's saved quote line Check date runs for (J-2, D-465 ruling 5). */
    quote_line_id: Uuid.optional(),
    allow_offcuts: z.boolean().optional(),
  })
  .strict()
  .openapi("CtpQuoteRequest");

/** Row 75. The line may be named here when the quote named none. */
export const CtpCommitRequest = z
  .object({ sales_order_line_id: Uuid.optional() })
  .strict()
  .openapi("CtpCommitRequest");

/** Row 76. A person's words, 1 to 500 characters. */
export const CtpWithdrawRequest = z
  .object({ reason: z.string().trim().min(1).max(500) })
  .strict()
  .openapi("CtpWithdrawRequest");

export const CtpPromiseIdParam = z.object({ id: Uuid });

/** Row 77. `status` repeats; without it the list serves the live statuses. */
export const CtpPromiseListQuery = z.object({
  facility_id: Uuid,
  status: z.union([PromiseStatus, z.array(PromiseStatus)]).optional(),
  customer_id: Uuid.optional(),
  product_id: Uuid.optional(),
  sales_order_id: Uuid.optional(),
  promised_from: PlanningDate.optional(),
  promised_to: PlanningDate.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const CtpPromiseListResponse = z
  .object({
    items: z.array(CtpPromise),
    next_cursor: z.string().nullable(),
  })
  .openapi("CtpPromiseListResponse");

// --- refusals -----------------------------------------------------------------------

/** 422 `CAPACITY_PATH_UNAVAILABLE`: the reasons and what stock alone covers. */
export const CtpUnavailableProblem = Problem.extend({
  reasons: z.array(CtpUnavailableReason).optional(),
  material: z
    .object({
      /** What on-hand stock and off-cuts alone cover, in the product's base unit. */
      covered_qty: PlanningQty,
      shortfall_qty: PlanningQty,
    })
    .optional(),
}).openapi("CtpUnavailableProblem");

/** 409 on a commit: the stale basis with its fresh successor, or the line's live promise. */
export const CtpCommitConflictProblem = Problem.extend({
  /** `BASIS_STALE`: the fresh quoted promise written in the same request (D-462 ruling 3). */
  fresh_promise: CtpPromise.optional(),
  /** `BASIS_STALE`: what moved, keys only. */
  stale_reasons: z.array(z.enum(["date_moved", "unit_unavailable", "unplaced"])).optional(),
  /** `LINE_ALREADY_PROMISED`: the line's live promise. */
  promise_id: Uuid.optional(),
  /** `PROMISE_NOT_QUOTED` and `ORDER_LINE_NOT_PROMISABLE`: the state met. */
  promise_status: PromiseStatus.optional(),
  reason: z
    .enum([
      "order_not_confirmed",
      // J-2 (D-465 ruling 1, item 63): the order's credit check is blocked.
      "credit_blocked",
      "line_cancelled",
      "line_shipped",
      "customer_mismatch",
      "product_mismatch",
      "facility_mismatch",
      "line_mismatch",
      "qty_short",
      // J-3 review (spec 09 §4.7): a re-promise over a line whose promise
      // the watch already judged kept.
      "promise_kept",
    ])
    .optional(),
}).openapi("CtpCommitConflictProblem");

/** 409 `PROMISE_NOT_WITHDRAWABLE` names the status met. */
export const CtpWithdrawConflictProblem = Problem.extend({
  promise_status: PromiseStatus.optional(),
}).openapi("CtpWithdrawConflictProblem");

// --- audit actions (§2.3 of the P3-J plan, spec 03 §5) -------------------------------

export const PROMISE_AUDIT_ACTIONS = {
  quoted: "promise.quoted",
  committed: "promise.committed",
  withdrawn: "promise.withdrawn",
  /** The watch's one correction, `broken → kept` (D-467 ruling 3), written by the system. */
  verdictCorrected: "promise.verdict_corrected",
} as const;
